import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axiosInstance from "../../services/axiosComman";

const SERIES = [
  {
    key: "completed",
    label: "Completed",
    statusFilter: "completed",
    color: "#008000", // Green
    hoverColor: "#006600",
    getPct: (item) => item?.completion_percentage ?? item?.completed_percentage ?? 0,
    getCount: (item) => item?.completed_count ?? 0,
  },
  {
    key: "in_progress",
    label: "In Progress",
    statusFilter: "in progress",
    color: "#ffd700", // Yellow / Gold
    hoverColor: "#e6c300",
    getPct: (item) => item?.in_progress_percentage ?? 0,
    getCount: (item) => item?.in_progress_count ?? 0,
  },
  {
    key: "yet_to_start",
    label: "Yet To Start",
    statusFilter: "yet to start",
    color: "#FF0000", // Red
    hoverColor: "#cc0000",
    getPct: (item) => item?.yet_to_start_percentage ?? 0,
    getCount: (item) => item?.yet_to_start_count ?? 0,
  },
];

function DashboardHeaderStatusView({
  url = "",
  title = "COMPLIANCE BY HEADER - Implementation Checklist",
  standardName = "",
  path = "",
}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredGroup, setHoveredGroup] = useState(null);
  const [hoveredBar, setHoveredBar] = useState(null);
  const cardRef = React.useRef(null);
  const navigate = useNavigate();

  async function getFunction() {
    if (!url) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get(url);
      if (res?.data?.data && Array.isArray(res.data.data)) {
        setData(res.data.data);
      } else {
        setData([]);
      }
    } catch (err) {
      console.error("Error fetching headerwise status:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getFunction();
  }, [url]);

  const handleBarClick = (item, statusFilter = null) => {
    if (path) {
      navigate(path, {
        state: {
          statusFilter: statusFilter || null,
          headerId: item?.header_id,
          headerName: item?.header_name,
        },
      });
    }
  };

  const formatPercentage = (val) => {
    const num = parseFloat(val);
    if (isNaN(num) || num <= 0) return "0%";
    return num % 1 === 0 ? `${num.toFixed(0)}%` : `${num.toFixed(1)}%`;
  };

  const getBarHeight = (val) => {
    const num = parseFloat(val);
    if (isNaN(num) || num <= 0) return 0;
    return Math.min(100, Math.max(0, num));
  };

  const displayTitle = standardName
    ? `${title} (${standardName.toUpperCase()})`
    : title;

  return (
    <div
      ref={cardRef}
      className="w-full bg-white rounded-xl shadow-main border border-[#E5E7EB] overflow-hidden flex flex-col relative"
    >
      {/* Top Banner */}
      <div className="bg-[#0B1536] text-white px-5 py-2.5 flex items-center justify-between min-h-[44px]">
        <h2 className="text-[13px] 2xl:text-[14px] font-bold tracking-wide uppercase">
          {displayTitle}
        </h2>
        {path && (
          <Link
            to={path}
            className="text-[11px] 2xl:text-xs text-[#9CA3AF] hover:text-white transition-colors underline"
          >
            View Checklist
          </Link>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-x-6 px-5 pt-3 pb-1">
        {SERIES.map((s) => (
          <div key={s.key} className="flex items-center gap-x-2 select-none">
            <span
              className="w-3.5 h-3.5 rounded-full inline-block shadow-sm"
              style={{ backgroundColor: s.color }}
            ></span>
            <span className="text-[12px] font-semibold text-[#4F4768]">
              {s.label}
            </span>
          </div>
        ))}
      </div>

      {/* Floating Tooltip outside overflow-x-auto scroll container */}
      {hoveredGroup && (
        <div
          style={{
            position: "absolute",
            left: `${hoveredGroup.left}px`,
            top: `${hoveredGroup.top - 6}px`,
            transform: "translate(-50%, -100%)",
            zIndex: 9999,
          }}
          className="bg-[#0B1536] text-white text-[11px] px-3 py-2 rounded-lg shadow-2xl whitespace-nowrap pointer-events-none border border-gray-700 transition-all duration-100"
        >
          <p className="font-bold border-b border-gray-700 pb-1 mb-1 text-center text-gray-200">
            {hoveredGroup.item.header_name}
          </p>
          <div className="space-y-0.5 text-[10px]">
            {SERIES.map((s) => {
              const p = s.getPct(hoveredGroup.item);
              const c = s.getCount(hoveredGroup.item);
              return (
                <div key={s.key} className="flex items-center gap-x-2">
                  <span
                    className="w-2 h-2 rounded-full inline-block"
                    style={{ backgroundColor: s.color }}
                  ></span>
                  <span className="text-gray-300">{s.label}:</span>
                  <span className="font-semibold text-white ml-auto">
                    {c} / {hoveredGroup.item.total_count ?? 0} ({formatPercentage(p)})
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Chart Container */}
      <div className="pe-4 pb-3 flex-1 flex flex-col justify-between">
        {loading ? (
          <div className="w-full h-64 flex items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-[#5945DB]"></div>
          </div>
        ) : !data || data.length === 0 ? (
          <div className="w-full h-64 flex flex-col items-center justify-center text-[#6E6985]">
            <p className="text-sm font-medium">No Header Data Available</p>
          </div>
        ) : (
          <div className="w-full relative flex items-stretch pt-2">
            {/* Fixed Y-Axis Column */}
            <div className="w-12 flex-shrink-0 flex flex-col pt-8 pr-2 border-r border-[#E5E7EB] select-none">
              <div className="h-[180px] flex flex-col justify-between text-right">
                <span className="text-[10px] 2xl:text-[11px] font-semibold text-[#6E6985] leading-none -translate-y-1/2">
                  100%
                </span>
                <span className="text-[10px] 2xl:text-[11px] font-semibold text-[#6E6985] leading-none -translate-y-1/2">
                  75%
                </span>
                <span className="text-[10px] 2xl:text-[11px] font-semibold text-[#6E6985] leading-none -translate-y-1/2">
                  50%
                </span>
                <span className="text-[10px] 2xl:text-[11px] font-semibold text-[#6E6985] leading-none -translate-y-1/2">
                  25%
                </span>
                <span className="text-[10px] 2xl:text-[11px] font-semibold text-[#6E6985] leading-none -translate-y-1/2">
                  0%
                </span>
              </div>
              {/* Bottom spacer aligning with header names below baseline */}
              <div className="h-[55px]"></div>
            </div>

            {/* Scrollable Chart Body */}
            <div
              className="flex-1 overflow-x-auto relative pt-8 pb-1 pl-4 pr-6"
              onScroll={() => setHoveredGroup(null)}
            >
              <div className="min-w-full w-max relative">
                {/* Horizontal Gridlines aligned with Y-ticks */}
                <div className="absolute inset-x-0 top-0 h-[180px] pointer-events-none flex flex-col justify-between z-0">
                  <div className="w-full border-b border-gray-100 border-dashed"></div>
                  <div className="w-full border-b border-gray-100 border-dashed"></div>
                  <div className="w-full border-b border-gray-100 border-dashed"></div>
                  <div className="w-full border-b border-gray-100 border-dashed"></div>
                  <div className="w-full border-b border-[#D1D5DB]"></div>
                </div>

                {/* Header Groups Row */}
                <div className="relative z-10 flex items-start gap-x-8">
                  {data.map((item, index) => {
                    return (
                      <div
                        key={item.header_id || index}
                        className="w-[90px] 2xl:w-[100px] flex-shrink-0 flex flex-col items-center cursor-pointer group relative"
                        onClick={() => handleBarClick(item)}
                        onMouseEnter={(e) => {
                          if (cardRef.current) {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const parentRect = cardRef.current.getBoundingClientRect();
                            setHoveredGroup({
                              item,
                              left: rect.left - parentRect.left + rect.width / 2,
                              top: rect.top - parentRect.top,
                            });
                          }
                        }}
                        onMouseLeave={() => {
                          setHoveredGroup(null);
                          setHoveredBar(null);
                        }}
                      >
                        {/* Clustered 3 Bars Container (fixed 180px height matching gridlines) */}
                        <div className="w-full h-[180px] flex items-end justify-center gap-x-1 px-1 relative">
                          {SERIES.map((s) => {
                            const pct = s.getPct(item);
                            const barHeight = getBarHeight(pct);
                            const numPct = parseFloat(pct) || 0;
                            const isThisBarHovered =
                              hoveredBar?.header_id === item.header_id &&
                              hoveredBar?.key === s.key;

                            return (
                              <div
                                key={s.key}
                                className="w-[22px] 2xl:w-[26px] flex flex-col justify-end items-center h-full relative cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBarClick(item, s.statusFilter);
                                }}
                                onMouseEnter={(e) => {
                                  e.stopPropagation();
                                  setHoveredBar({ header_id: item.header_id, key: s.key });
                                }}
                              >
                                {/* The Colored Bar */}
                                <div
                                  className="w-full rounded-t-sm transition-all duration-300 ease-out relative shadow-sm"
                                  style={{
                                    height: `${barHeight}%`,
                                    backgroundColor: isThisBarHovered ? s.hoverColor : s.color,
                                    minHeight: numPct > 0 ? "4px" : "1px",
                                  }}
                                >
                                  {/* Percentage Label on Top of Bar */}
                                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 text-[9px] 2xl:text-[10px] font-bold text-[#0B1536] whitespace-nowrap group-hover:scale-105 transition-transform">
                                    {formatPercentage(pct)}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* X-Axis Header Label below baseline */}
                        <div className="w-full h-[55px] pt-2 px-0.5 flex items-start justify-center">
                          <span
                            className="text-[11px] 2xl:text-[12px] font-medium text-[#4F4768] text-center leading-tight group-hover:text-primary transition-colors line-clamp-3"
                            title={item.header_name}
                          >
                            {item.header_name}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DashboardHeaderStatusView;
